import {
  namespaceCreator as createAccessibilityNamespace,
  INamespaceDefinition as IAccessibilityNamespaceDefinition,
  createSuperAPIFactory as createAccessibilitySuperAPIFactory,
  IHostAPI as IAccessibilityHostAPI,
} from './accessibility';
import {
  namespaceCreator as createRefComponentsNamespace,
  INamespaceDefinition as IRefComponentsNamespaceDefinition,
  createSuperAPIFactory as createRefComponentsSuperAPIFactory,
  IHostAPI as IRefComponentsHostAPI,
} from './refComponents';
import {
  IContextBasedFactory,
  IDocumentServices,
  IHostType,
  IUnionToIntersection,
  IFindByHostType,
  IConsumerType,
  IAPIVersions,
  IContext,
} from '../common';

type INamespaceDefinition =
  | IAccessibilityNamespaceDefinition
  | IRefComponentsNamespaceDefinition;

type IMainSuperAPI<T extends IHostType> = IUnionToIntersection<
  IFindByHostType<INamespaceDefinition, T>['superAPI']
>;

export type IMainSuperAPIFactory<T extends IHostType> = IContextBasedFactory<
  IMainSuperAPI<T>
>;

export type IPlatformAPI<
  T extends IConsumerType,
  P extends IAPIVersions,
> = Extract<
  INamespaceDefinition,
  { consumerType: T; version: P }
>['platformAPI'];

export type IMainHostAPI<T extends IHostType> = IRefComponentsHostAPI<T> &
  IAccessibilityHostAPI<T>;

export type IPlatformAPIFactory<T extends IConsumerType> = <
  P extends IAPIVersions,
>(
  context: IContext,
  version: P,
) => IPlatformAPI<T, P>;

export function createSuperAPIFactories<T extends IHostType>(
  hostType: T,
  hostAPI: IMainHostAPI<T>,
  dsAPI: IDocumentServices,
): IMainSuperAPIFactory<T> {
  return (context) => {
    return {
      ...createAccessibilitySuperAPIFactory<T>(
        hostType,
        hostAPI,
        dsAPI,
      )(context),
      ...createRefComponentsSuperAPIFactory<T>(
        hostType,
        hostAPI,
        dsAPI,
      )(context),
    } as IMainSuperAPI<T>;
  };
}

export function createPlatformAPI<
  T extends IConsumerType,
  K extends IHostType,
  P extends IAPIVersions,
>(
  context: IContext,
  hostType: K,
  consumerType: T,
  version: P,
  superAPIFactory: IMainSuperAPIFactory<K>,
) {
  const superAPI = superAPIFactory(context);

  if (hostType === 'classic') {
    // classic
    return {
      ...createAccessibilityNamespace({
        context,
        consumerType,
        hostType,
        version,
        superAPI: superAPI as IMainSuperAPI<'classic'>,
      }),
      ...createRefComponentsNamespace({
        context,
        consumerType,
        hostType,
        version,
        superAPI: superAPI as IMainSuperAPI<'classic'>,
      }),
    } as IPlatformAPI<T, P>;
  } else {
    // editorx
    return {
      ...createAccessibilityNamespace({
        context,
        consumerType,
        hostType,
        version,
        superAPI: superAPI as IMainSuperAPI<'editorx'>,
      }),
      ...createRefComponentsNamespace({
        context,
        consumerType,
        hostType,
        version,
        superAPI: superAPI as IMainSuperAPI<'editorx'>,
      }),
    } as IPlatformAPI<T, P>;
  }
}
